import { SectionData } from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { useCallback, useContext, useState } from 'react';
import { uid } from 'uid';
import Modal from '../components/Common/Modal';
import TextField from '../components/Common/TextField';
import Toggle from '../components/Common/Toggle';
import ToggleButton from '../components/Common/ToggleButton';
import { AuthContext } from '../contexts/AuthContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { UserContext } from '../contexts/UserContext';
import { createFavoriteSection } from '../services/SectionsService';

interface FavoriteSectionConfig {
  name: string;
  level: 'account' | 'campaign';
  saveContent: boolean;
  saveImages: boolean;
}

const createDefaultFavoriteSectionConfig = (): FavoriteSectionConfig => ({
  name: '',
  level: 'account',
  saveContent: true,
  saveImages: true,
});

interface Props {
  section?: Partial<SectionData>;
  open: boolean;
  onClose: () => void;
}

export default function AddFavoriteSectionModal({
  section,
  open,
  onClose,
}: Props) {
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const [favoriteSectionConfig, setFavoriteSectionConfig] =
    useState<FavoriteSectionConfig>(createDefaultFavoriteSectionConfig);

  const _onClose = useCallback(() => {
    setError(undefined);
    setLoading(false);
    setFavoriteSectionConfig(createDefaultFavoriteSectionConfig());
    onClose();
  }, [onClose]);

  const onAddClick = useCallback(() => {
    if (!open) return;

    if (!section || !section.type || !section.variant) {
      setError('Something went wrong. Please try again.');
      return;
    }

    if (!selectedAccount || !selectedDynamicPage) {
      setError('Something went wrong. Please try again.');
      return;
    }

    const newSection: Partial<SectionData> = {
      ...section,
    };

    if (!favoriteSectionConfig.saveContent) {
      delete newSection.title;
      delete newSection.tagline;
      delete newSection.subtitle;

      if ('text' in newSection) {
        delete newSection.text;
      }

      if ('description' in newSection) {
        delete newSection.description;
      }

      if ('items' in newSection) {
        delete newSection.items;
      }

      if ('features' in newSection) {
        delete newSection.features;
      }

      if ('testimonials' in newSection) {
        delete newSection.testimonials;
      }

      if ('pressMentions' in newSection) {
        delete newSection.pressMentions;
      }

      if ('logos' in newSection) {
        delete newSection.logos;
      }

      if ('cta' in newSection) {
        delete newSection.cta;
      }
    }

    if (!favoriteSectionConfig.saveImages) {
      if ('image' in newSection) {
        delete newSection.image;
      }
    }

    if ('formData' in newSection) {
      delete newSection.formData;
    }

    if ('formId' in newSection) {
      delete newSection.formId;
    }

    setLoading(true);
    createFavoriteSection(
      selectedAccount.id,
      favoriteSectionConfig.level === 'campaign' ? selectedDynamicPage.id : '',
      favoriteSectionConfig.name,
      { ...newSection, id: uid(12) },
      firebaseUser?.uid || '',
    )
      .then(() => {
        _onClose();
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    open,
    section,
    selectedAccount,
    selectedDynamicPage,
    favoriteSectionConfig.saveContent,
    favoriteSectionConfig.saveImages,
    favoriteSectionConfig.level,
    favoriteSectionConfig.name,
    firebaseUser?.uid,
    _onClose,
  ]);

  return (
    <Modal
      open={open}
      onClose={_onClose}
      actions={[
        {
          title: 'Cancel',
          type: 'secondary',
          disabled: loading,
          onClick: _onClose,
        },
        {
          title: 'Create',
          type: 'primary',
          loading: loading,
          onClick: onAddClick,
        },
      ]}
    >
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        Add Section to Favorites
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-4">
        <TextField
          title="Name (Optional)"
          value={favoriteSectionConfig.name}
          onChange={(e) =>
            setFavoriteSectionConfig({
              ...favoriteSectionConfig,
              name: e.target.value,
            })
          }
        />

        <div>
          <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
            Save to
          </label>

          <ToggleButton<FavoriteSectionConfig['level']>
            value={favoriteSectionConfig.level}
            items={[
              { value: 'account', title: 'Account' },
              { value: 'campaign', title: 'Dynamic Page' },
            ]}
            onChange={(value) =>
              setFavoriteSectionConfig({
                ...favoriteSectionConfig,
                level: value,
              })
            }
          />
        </div>

        <Toggle
          label="Save Content"
          checked={favoriteSectionConfig.saveContent}
          onChange={(checked) => {
            setFavoriteSectionConfig({
              ...favoriteSectionConfig,
              saveContent: checked,
            });
          }}
        />

        <Toggle
          label="Save Images"
          checked={favoriteSectionConfig.saveImages}
          onChange={(checked) => {
            setFavoriteSectionConfig({
              ...favoriteSectionConfig,
              saveImages: checked,
            });
          }}
        />
      </div>
      {error ? (
        <p className="mt-4 w-full text-end text-sm text-red-600">{error}</p>
      ) : (
        <></>
      )}
    </Modal>
  );
}
